import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BASE_URL } from '../Config';
import keepaiLogo from '../resource/keepaiLogo.png';
import '../css/style/chat.css';

const getAuth = () => {
    const stored = localStorage.getItem('user');
    if (!stored) return null;
    return JSON.parse(stored);
};

const authHeader = () => {
    const user = getAuth();
    return user?.token ? { Authorization: `Bearer ${user.token}` } : {};
};

const Chat = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const initialQuery = location.state?.query || '';
    const initialSessionId = location.state?.sessionId || crypto.randomUUID();

    const [sessionId] = useState(initialSessionId);
    const [messages, setMessages] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const chatBodyRef = useRef(null);
    const textareaRef = useRef(null);
    const user = getAuth();

    const scrollToBottom = useCallback(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    }, []);

    const loadSessions = useCallback(async () => {
        if (!user?.token) return;
        try {
            const res = await fetch(`${BASE_URL}/chat/sessions`, { headers: authHeader() });
            const data = await res.json();
            setSessions(data.sessions || []);
        } catch {}
    }, []);

    const loadHistory = useCallback(async (sid) => {
        if (!user?.token) return;
        try {
            const res = await fetch(`${BASE_URL}/chat/history/${sid}`, { headers: authHeader() });
            const data = await res.json();
            setMessages(data.messages || []);
        } catch {}
    }, []);

    useEffect(() => {
        if (user?.token) {
            loadSessions();
            if (location.state?.sessionId) {
                loadHistory(location.state.sessionId);
            } else if (initialQuery) {
                sendMessage(initialQuery);
            }
        } else if (initialQuery) {
            sendMessage(initialQuery);
        }
    }, []); // eslint-disable-line

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    const saveMessage = async (role, content) => {
        if (!user?.token) return;
        try {
            await fetch(`${BASE_URL}/chat/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ sessionId, role, content }),
            });
        } catch {}
    };

    const sendMessage = async (text) => {
        const userText = text ?? input.trim();
        if (!userText || loading) return;

        const newMessages = [...messages, { role: 'user', content: userText }];
        setMessages(newMessages);
        setInput('');
        setLoading(true);
        if (textareaRef.current) textareaRef.current.style.height = 'auto';

        await saveMessage('user', userText);

        const assistantIndex = newMessages.length;
        setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
        let fullResponse = '';
        let rafId = null;

        try {
            const res = await fetch(`${BASE_URL}/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messages: newMessages }),
            });

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            const flushUpdate = () => {
                const snapshot = fullResponse;
                setMessages(prev => {
                    const updated = [...prev];
                    updated[assistantIndex] = { ...updated[assistantIndex], content: snapshot };
                    return updated;
                });
                rafId = null;
            };

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();

                let hasNew = false;
                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    const data = line.slice(6).trim();
                    if (data === '[DONE]') break;
                    try {
                        const delta = JSON.parse(data).choices?.[0]?.delta?.content;
                        if (delta) {
                            fullResponse += delta;
                            hasNew = true;
                        }
                    } catch {}
                }

                // rAF로 묶어서 프레임당 1회만 re-render
                if (hasNew && !rafId) {
                    rafId = requestAnimationFrame(flushUpdate);
                }
            }

            // 남은 업데이트 강제 반영
            if (rafId) cancelAnimationFrame(rafId);
            if (fullResponse) {
                setMessages(prev => {
                    const updated = [...prev];
                    updated[assistantIndex] = { ...updated[assistantIndex], content: fullResponse };
                    return updated;
                });
                await saveMessage('assistant', fullResponse);
            }
        } catch {
            if (rafId) cancelAnimationFrame(rafId);
            setMessages(prev => {
                const updated = [...prev];
                updated[assistantIndex] = { role: 'assistant', content: '오류가 발생했습니다. 다시 시도해주세요.' };
                return updated;
            });
        } finally {
            setLoading(false);
            loadSessions();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleInput = (e) => {
        setInput(e.target.value);
        const el = textareaRef.current;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 160) + 'px';
    };

    const startNewChat = () => {
        navigate('/chat', { state: { sessionId: crypto.randomUUID() } });
        window.location.reload();
    };

    const openSession = (sid) => {
        navigate('/chat', { state: { sessionId: sid } });
        window.location.reload();
    };

    return (
        <div className="chat-page">
            {/* 사이드바 */}
            {sidebarOpen && (
                <>
                    <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
                    <aside className="chat-sidebar">
                        <div className="sidebar-header">
                            <span>대화 기록</span>
                            <button className="sidebar-new-btn" onClick={startNewChat}>+ 새 대화</button>
                        </div>
                        <ul className="session-list">
                            {sessions.length === 0 && <li className="session-empty">대화 기록이 없습니다</li>}
                            {sessions.map(s => (
                                <li
                                    key={s.SESSION_ID}
                                    className={`session-item ${s.SESSION_ID === sessionId ? 'active' : ''}`}
                                    onClick={() => openSession(s.SESSION_ID)}
                                >
                                    <span className="session-preview">{s.preview?.slice(0, 30) || '새 대화'}</span>
                                    <span className="session-date">{new Date(s.last_at).toLocaleDateString('ko-KR')}</span>
                                </li>
                            ))}
                        </ul>
                    </aside>
                </>
            )}

            {/* 상단 바 */}
            <header className="chat-header">
                <div className="chat-header-left">
                    {user?.token && (
                        <button className="sidebar-toggle" onClick={() => setSidebarOpen(v => !v)}>☰</button>
                    )}
                    <img src={keepaiLogo} alt="KeepAi" className="chat-logo" onClick={() => navigate('/')} />
                </div>
                <span className="chat-model-badge">GPT-5 mini</span>
            </header>

            {/* 메시지 영역 */}
            <div className="chat-body" ref={chatBodyRef}>
                <div className="chat-messages-inner">
                    {messages.length === 0 && (
                        <div className="chat-empty">
                            <div className="chat-empty-icon">💬</div>
                            <p>무엇이든 물어보세요</p>
                        </div>
                    )}
                    {messages.map((msg, i) => (
                        <div key={i} className={`chat-message ${msg.role}`}>
                            {msg.role === 'assistant' && <div className="chat-avatar">K</div>}
                            <div className="chat-bubble">
                                {msg.content || (loading && i === messages.length - 1
                                    ? <span className="typing-dot">
                                        <span /><span /><span />
                                      </span>
                                    : ''
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 입력 영역 */}
            <div className="chat-input-wrap">
                <div className="chat-input-box">
                    <textarea
                        ref={textareaRef}
                        rows={1}
                        value={input}
                        onChange={handleInput}
                        onKeyDown={handleKeyDown}
                        placeholder="메시지를 입력하세요... (Enter 전송, Shift+Enter 줄바꿈)"
                        disabled={loading}
                    />
                    <button
                        className="chat-send-btn"
                        onClick={() => sendMessage()}
                        disabled={loading || !input.trim()}
                    >↑</button>
                </div>
                <p className="chat-disclaimer">AI가 생성한 내용은 사실과 다를 수 있습니다.</p>
            </div>
        </div>
    );
};

export default Chat;
